import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const IssuerRegistry = ({ issuers, onVerifyIssuer, onViewIssuerDetails }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const filteredIssuers = issuers?.filter(issuer => {
    const matchesSearch = issuer?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         issuer?.did?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         issuer?.domain?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesFilter = filterStatus === 'all' || issuer?.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return { name: 'CheckCircle', color: 'text-success' };
      case 'pending':
        return { name: 'Clock', color: 'text-warning' };
      case 'revoked':
        return { name: 'XCircle', color: 'text-error' };
      default:
        return { name: 'AlertCircle', color: 'text-muted-foreground' };
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case 'verified':
        return `${baseClasses} bg-success/10 text-success`;
      case 'pending':
        return `${baseClasses} bg-warning/10 text-warning`;
      case 'revoked':
        return `${baseClasses} bg-error/10 text-error`;
      default:
        return `${baseClasses} bg-muted text-muted-foreground`;
    }
  };

  const getTrustScoreColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 70) return 'text-warning';
    return 'text-error';
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Issuer Registry</h2>
          <p className="text-muted-foreground">Trusted credential issuers and their verification status</p>
        </div>
        <Button variant="outline" size="sm">
          <Icon name="Plus" size={16} className="mr-2" />
          Add Issuer
        </Button>
      </div>
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Input
          type="search"
          placeholder="Search issuers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e?.target?.value)}
        />
        
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e?.target?.value)}
          className="px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="all">All Status</option>
          <option value="verified">Verified</option>
          <option value="pending">Pending</option>
          <option value="revoked">Revoked</option>
        </select>
      </div>
      {/* Issuer List */}
      <div className="space-y-4">
        {filteredIssuers?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Building" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No issuers found</p>
          </div>
        ) : (
          filteredIssuers?.map((issuer) => {
            const statusIcon = getStatusIcon(issuer?.status);
            
            return (
              <div
                key={issuer?.id}
                className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-clinical"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    {/* Issuer Logo/Icon */}
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="Building" size={24} className="text-primary" />
                    </div>

                    {/* Issuer Details */}
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-medium text-foreground">{issuer?.name}</h3>
                        <span className={getStatusBadge(issuer?.status)}>
                          {issuer?.status?.charAt(0)?.toUpperCase() + issuer?.status?.slice(1)}
                        </span>
                        <div className="flex items-center space-x-1">
                          <Icon name="Star" size={14} className={getTrustScoreColor(issuer?.trustScore)} />
                          <span className={`text-sm font-medium ${getTrustScoreColor(issuer?.trustScore)}`}>
                            {issuer?.trustScore}%
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2 text-sm text-muted-foreground">
                        <div className="flex items-center space-x-2">
                          <Icon name="Globe" size={14} />
                          <span>{issuer?.domain}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Icon name="Key" size={14} />
                          <span className="font-mono text-xs">{issuer?.did}</span>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-1">
                            <Icon name="Calendar" size={14} />
                            <span>Registered: {formatDate(issuer?.registrationDate)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Icon name="FileText" size={14} />
                            <span>{issuer?.credentialsIssued} credentials issued</span>
                          </div>
                        </div>
                      </div>

                      {/* Credential Types */}
                      <div className="mt-3">
                        <p className="text-sm font-medium text-foreground mb-2">Authorized Credential Types:</p>
                        <div className="flex flex-wrap gap-2">
                          {issuer?.authorizedTypes?.map((type, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 bg-muted rounded-full text-xs text-muted-foreground"
                            >
                              {type}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onVerifyIssuer(issuer?.id)}
                    >
                      <Icon name="Shield" size={14} className="mr-1" />
                      Verify
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewIssuerDetails(issuer)}
                    >
                      <Icon name="Eye" size={14} className="mr-1" />
                      Details
                    </Button>
                  </div>
                </div>
                {/* Smart Contract Info */}
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                    <div>
                      <label className="font-medium text-muted-foreground">Smart Contract</label>
                      <p className="text-foreground font-mono mt-1">{issuer?.smartContract}</p>
                    </div>
                    <div>
                      <label className="font-medium text-muted-foreground">Network</label>
                      <p className="text-foreground mt-1">{issuer?.network}</p>
                    </div>
                    <div>
                      <label className="font-medium text-muted-foreground">Last Verification</label>
                      <p className="text-foreground mt-1">{formatDate(issuer?.lastVerification)}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      {/* Registry Stats */}
      {issuers?.length > 0 && (
        <div className="border-t border-border pt-6 mt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-semibold text-foreground">
                {issuers?.length}
              </p>
              <p className="text-sm text-muted-foreground">Total Issuers</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-semibold text-success">
                {issuers?.filter(i => i?.status === 'verified')?.length}
              </p>
              <p className="text-sm text-muted-foreground">Verified</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-semibold text-warning">
                {issuers?.filter(i => i?.status === 'pending')?.length}
              </p>
              <p className="text-sm text-muted-foreground">Pending</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-semibold text-foreground">
                {issuers?.reduce((sum, issuer) => sum + issuer?.credentialsIssued, 0)}
              </p>
              <p className="text-sm text-muted-foreground">Credentials Issued</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IssuerRegistry;